/**
 * Comentarios anclados al canvas.
 *
 * El punto de anclaje vive en coordenadas de mundo y el componente entero usa
 * el zoom normal del canvas, igual que un nodo de texto: acercarse lo agranda
 * y alejarse lo achica. La capa no recibe eventos salvo el textarea durante la
 * edición; selección, hover y arrastre siguen entrando por la máquina de
 * gestos de `CanvasView`.
 */

export interface CommentItem {
  id: string;
  x: number;
  y: number;
  text: string;
  pinned: boolean;
  /** `true` compensa el zoom sólo en la burbuja; el Anchor no lo consulta. */
  bodyAutoScale: boolean;
  selected: boolean;
}

interface CommentEntry {
  root: HTMLElement;
  anchor: HTMLElement;
  bubbleScale: HTMLElement;
  bubble: HTMLElement;
  body: HTMLElement;
  text: string;
  left: number;
  top: number;
  zoom: number;
  bodyCompensation: number;
}

export interface CommentEditorHandlers {
  onChange: (id: string, text: string) => void;
  onClose: (id: string) => void;
}

/* Burbuja con la cola centrada abajo: dentro del círculo de 32 px el glifo
   queda ópticamente centrado, sin el sesgo hacia abajo-izquierda que tenía la
   cola diagonal. */
const COMMENT_SVG =
  '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4.75 6.5a2 2 0 0 1 2-2h10.5a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3.1L12 18.2 9.85 15.5H6.75a2 2 0 0 1-2-2Z"/><path d="M8.25 8.75h7.5M8.25 11.5h4.75"/></svg>';

function contains(rect: DOMRect, x: number, y: number): boolean {
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

/** Corredor que une dos rects, para poder mover el puntero del ancla a la burbuja. */
function bridgeContains(a: DOMRect, b: DOMRect, x: number, y: number): boolean {
  return (
    x >= Math.min(a.left, b.left) &&
    x <= Math.max(a.right, b.right) &&
    y >= Math.min(a.top, b.top) &&
    y <= Math.max(a.bottom, b.bottom)
  );
}

export class CommentLayer {
  readonly root: HTMLElement;
  private entries = new Map<string, CommentEntry>();
  private items = new Map<string, CommentItem>();
  private hoveredId: string | null = null;
  private editId: string | null = null;
  private editor: HTMLTextAreaElement | null = null;
  private handlers: CommentEditorHandlers | null = null;
  private pendingEdit: { id: string; text: string; handlers: CommentEditorHandlers } | null = null;

  constructor() {
    this.root = document.createElement("div");
    this.root.className = "canvas-comments";
  }

  get editingId(): string | null {
    return this.editId ?? this.pendingEdit?.id ?? null;
  }

  sync(
    items: CommentItem[],
    worldToScreen: (x: number, y: number) => { x: number; y: number },
    zoom: number,
  ): void {
    const live = new Set<string>();
    this.items = new Map(items.map((item) => [item.id, item]));

    for (const item of items) {
      live.add(item.id);
      let entry = this.entries.get(item.id);
      if (!entry) {
        const root = document.createElement("div");
        root.className = "canvas-comment";
        root.dataset.commentId = item.id;

        const anchor = document.createElement("div");
        anchor.className = "comment-anchor";
        anchor.innerHTML = COMMENT_SVG;

        // Wrapper EXCLUSIVO del body. Separarlo del root es la garantía
        // estructural de que compensar el zoom nunca toque el Anchor.
        const bubbleScale = document.createElement("div");
        bubbleScale.className = "comment-bubble-scale";
        const bubble = document.createElement("div");
        bubble.className = "comment-bubble";
        const body = document.createElement("div");
        body.className = "comment-body";
        bubble.appendChild(body);
        bubbleScale.appendChild(bubble);
        root.append(anchor, bubbleScale);
        this.root.appendChild(root);
        entry = {
          root,
          anchor,
          bubbleScale,
          bubble,
          body,
          text: "",
          left: NaN,
          top: NaN,
          zoom: NaN,
          bodyCompensation: NaN,
        };
        this.entries.set(item.id, entry);
      }

      if (entry.text !== item.text) {
        entry.text = item.text;
        entry.body.textContent = item.text;
      }

      const editing = this.editingId === item.id;
      entry.root.classList.toggle("is-selected", item.selected);
      entry.root.classList.toggle("is-pinned", item.pinned);
      entry.root.classList.toggle("is-hovered", this.hoveredId === item.id);
      entry.root.classList.toggle("is-editing", editing);
      entry.root.classList.toggle("is-empty", item.text.trim().length === 0);

      const p = worldToScreen(item.x, item.y);
      const left = Math.round(p.x);
      const top = Math.round(p.y);
      // Misma receta que `CanvasTextLabels`: posición de pantalla + escala de
      // mundo sobre todo el nodo. No hay compensación ni escala inversa.
      if (left !== entry.left || top !== entry.top || zoom !== entry.zoom) {
        entry.left = left;
        entry.top = top;
        entry.zoom = zoom;
        entry.root.style.left = `${left}px`;
        entry.root.style.top = `${top}px`;
        entry.root.style.transform = `scale(${zoom})`;
      }
      // El root ya trae `zoom`. Multiplicarlo por `1 / zoom` únicamente en
      // este wrapper deja el body a escala de pantalla 1, sin reutilizar ni
      // alterar ningún elemento del Anchor. Su origen está en el centro
      // vertical del Anchor (CSS), así que escalar no descentra la burbuja.
      const bodyCompensation = item.bodyAutoScale ? 1 / Math.max(zoom, 0.0001) : 1;
      if (bodyCompensation !== entry.bodyCompensation) {
        entry.bodyCompensation = bodyCompensation;
        entry.bubbleScale.style.transform = `scale(${bodyCompensation})`;
      }
    }

    for (const [id, entry] of this.entries) {
      if (live.has(id)) continue;
      if (this.editingId === id) this.closeEditor();
      entry.root.remove();
      this.entries.delete(id);
      if (this.hoveredId === id) this.hoveredId = null;
    }

    if (this.pendingEdit) {
      const pending = this.pendingEdit;
      this.pendingEdit = null;
      this.mountEditor(pending.id, pending.text, pending.handlers);
    }
  }

  /** Actualiza qué comentario debe desplegarse bajo el puntero. */
  hoverAt(screenX: number, screenY: number): void {
    if (this.editingId) return;
    const rootRect = this.root.getBoundingClientRect();
    const x = screenX + rootRect.left;
    const y = screenY + rootRect.top;
    let next: string | null = null;
    const entries = [...this.entries.entries()];
    for (let i = entries.length - 1; i >= 0; i--) {
      const [id, entry] = entries[i];
      const item = this.items.get(id);
      if (!item) continue;
      if (contains(entry.anchor.getBoundingClientRect(), x, y)) {
        next = id;
        break;
      }
      // Mientras ya está abierto por hover, todo el corredor entre el círculo
      // y la burbuja cuenta: sin esto los 10 px de aire la cerraban antes de
      // que el puntero pudiera llegar al mensaje.
      const open = id === this.hoveredId || item.pinned || item.selected;
      const bubbleRect = entry.bubble.getBoundingClientRect();
      const staysHovered =
        id === this.hoveredId &&
        bridgeContains(entry.anchor.getBoundingClientRect(), bubbleRect, x, y);
      if (open && (contains(bubbleRect, x, y) || staysHovered)) {
        next = id;
        break;
      }
    }
    if (next === this.hoveredId) return;
    const previous = this.hoveredId;
    this.hoveredId = next;
    if (previous) this.entries.get(previous)?.root.classList.remove("is-hovered");
    if (next) this.entries.get(next)?.root.classList.add("is-hovered");
  }

  /** Comentario visible bajo el punto, priorizando el que esté más arriba. */
  hitAt(screenX: number, screenY: number): string | null {
    const rootRect = this.root.getBoundingClientRect();
    const x = screenX + rootRect.left;
    const y = screenY + rootRect.top;
    const entries = [...this.entries.entries()];
    for (let i = entries.length - 1; i >= 0; i--) {
      const [id, entry] = entries[i];
      if (contains(entry.anchor.getBoundingClientRect(), x, y)) return id;
      const item = this.items.get(id);
      const open = item && (item.pinned || item.selected || id === this.hoveredId || id === this.editingId);
      if (open && contains(entry.bubble.getBoundingClientRect(), x, y)) return id;
    }
    return null;
  }

  startEdit(id: string, text: string, handlers: CommentEditorHandlers): void {
    this.closeEditor();
    if (!this.entries.has(id)) {
      this.pendingEdit = { id, text, handlers };
      return;
    }
    this.mountEditor(id, text, handlers);
  }

  private mountEditor(id: string, text: string, handlers: CommentEditorHandlers): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    this.editId = id;
    this.handlers = handlers;
    entry.root.classList.add("is-editing");

    const editor = document.createElement("textarea");
    editor.className = "comment-editor";
    editor.value = text;
    editor.placeholder = "Write a comment…";
    editor.spellcheck = true;
    editor.addEventListener("keydown", (event) => {
      event.stopPropagation();
      if (event.key === "Escape" || (event.key === "Enter" && (event.metaKey || event.ctrlKey))) {
        event.preventDefault();
        this.closeEditor();
      }
    });
    // Sin `resize` ni scroll, la altura la lleva el contenido: reiniciar a
    // `auto` antes de medir es lo que permite también encoger al borrar.
    const autoGrow = () => {
      editor.style.height = "auto";
      editor.style.height = `${editor.scrollHeight}px`;
    };
    editor.addEventListener("input", () => {
      autoGrow();
      handlers.onChange(id, editor.value);
    });
    editor.addEventListener("blur", () => this.closeEditor());
    editor.addEventListener("pointerdown", (event) => event.stopPropagation());
    entry.bubble.appendChild(editor);
    this.editor = editor;
    autoGrow();
    editor.focus();
    editor.select();
  }

  closeEditor(): void {
    const id = this.editId;
    const handlers = this.handlers;
    this.editor?.remove();
    this.editor = null;
    this.editId = null;
    this.handlers = null;
    this.pendingEdit = null;
    if (id) {
      this.entries.get(id)?.root.classList.remove("is-editing");
      handlers?.onClose(id);
    }
  }

  destroy(): void {
    this.closeEditor();
    for (const entry of this.entries.values()) entry.root.remove();
    this.entries.clear();
    this.root.remove();
  }
}
